import type { iconTypes } from "skedyul-ui/ui";
import { GetPageResponse } from "@store/features/crm/pageV2Slice";
import { DataType } from "skedyul-db/browser";
import { Params } from "../../helpers";
import { getByPath, getErrorForRowAndPath } from "./utils";

export const createSelectCell = (
  field: {
    type: DataType;
    definition?: {
      options?: Array<{
        value: string;
        label: string;
        color?: string | null;
        icon?: string | null;
      }>;
      limitChoices?: number | null;
    } | null;
    label?: string;
  },
  _params: Params,
  accessorPath: string,
  pageData?: GetPageResponse,
): {
  component: "Select";
  generateProps: (
    row: Record<string, unknown>,
    _index: number,
    originalRow?: Record<string, unknown>,
  ) => {
    id: string;
    value: string;
    originalValue: string;
    items: Array<{
      value: string;
      label: string;
      color?: string | null;
      icon?: iconTypes;
    }>;
    fieldError: { code: string; message: string } | undefined;
    handleChange: (value: string) => void;
    isDisabled?: boolean;
    infoText?: string;
    emptyLabel?: string;
  };
} => {
  return {
    component: "Select" as const,
    generateProps: (
      row: Record<string, unknown>,
      _index: number,
      originalRow?: Record<string, unknown>,
    ) => {
      const currentValue = getByPath(row, accessorPath);
      const originalValue = originalRow
        ? getByPath(originalRow, accessorPath)
        : undefined;

      const meta = row[`${accessorPath}__meta`];
      const lock =
        meta && typeof meta === "object" && !Array.isArray(meta)
          ? (meta as {
              locked?: boolean;
              reason?: string | null;
              emptyLabel?: string;
            })
          : null;
      const locked = lock?.locked === true;
      const result: {
        id: string;
        value: string;
        originalValue: string;
        items: Array<{
          value: string;
          label: string;
          color?: string | null;
          icon?: iconTypes;
        }>;
        fieldError: { code: string; message: string } | undefined;
        handleChange: (value: string) => void;
        isDisabled?: boolean;
        infoText?: string;
        emptyLabel?: string;
      } = {
        id: accessorPath,
        value:
          typeof currentValue === "string"
            ? currentValue
            : String(currentValue ?? ""),
        originalValue:
          typeof originalValue === "string"
            ? originalValue
            : String(originalValue ?? ""),
        items:
          field.definition?.options?.map((o) => ({
            value: o.value,
            label: o.label,
            color: o.color,
            icon: o.icon as iconTypes | undefined,
          })) || [],
        fieldError: getErrorForRowAndPath(row, accessorPath, pageData || null),
        handleChange: (value: string) => {},
        isDisabled: locked,
        ...(locked && typeof lock?.reason === "string" && lock.reason
          ? { infoText: lock.reason }
          : {}),
        ...(locked && lock?.emptyLabel ? { emptyLabel: lock.emptyLabel } : {}),
      };

      return result;
    },
  };
};
